import React, { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import toast from "react-hot-toast";
import {
  getFileTypeFromFile,
  maxPatrolDrawingUploadBytes,
  PATROL_DRAWING_IMAGE_MAX_BYTES,
  PATROL_DRAWING_PDF_MAX_BYTES,
} from "./patrolDrawingMedia.js";
import PatrolDrawingMediaSurface from "./PatrolDrawingMediaSurface.jsx";

const ALLOWED_EXT = [".jpg", ".jpeg", ".png", ".pdf"];
function isAllowedFile(file) {
  const name = file.name.toLowerCase();
  return ALLOWED_EXT.some((ext) => name.endsWith(ext));
}

/**
 * @param {{ open: boolean, onClose: () => void, onSave: (payload: object) => void | Promise<void>, existingTitles?: string[] }} props
 */
export default function AddPatrolDrawingDialog({
  open,
  onClose,
  onSave,
  existingTitles = [],
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fileObjectUrl, setFileObjectUrl] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    isFavorite: false,
  });

  useEffect(() => {
    if (!uploadedFile) {
      setFileObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(uploadedFile);
    setFileObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadedFile]);

  const reset = useCallback(() => {
    setCurrentStep(1);
    setUploadedFile(null);
    setValidationError(null);
    setFormData({ title: "", description: "", isFavorite: false });
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const validateFile = useCallback(
    (file) => {
      if (!isAllowedFile(file)) {
        return "File must be .jpg, .jpeg, .png, or .pdf.";
      }
      const maxBytes = maxPatrolDrawingUploadBytes(file);
      if (file.size > maxBytes) {
        const isPdf = file.name.toLowerCase().endsWith(".pdf");
        const limitMb = isPdf
          ? PATROL_DRAWING_PDF_MAX_BYTES / (1024 * 1024)
          : PATROL_DRAWING_IMAGE_MAX_BYTES / (1024 * 1024);
        const kind = isPdf ? "PDF" : "Image";
        return `${kind} must not exceed ${limitMb}MB.`;
      }
      const title = formData.title.trim() || file.name.replace(/\.[^.]+$/, "");
      if (
        existingTitles.some(
          (t) => t.trim().toLowerCase() === title.toLowerCase(),
        )
      ) {
        return "A drawing with this name already exists.";
      }
      return null;
    },
    [existingTitles, formData.title],
  );

  const handleFileSelection = useCallback(
    (file) => {
      setValidationError(null);
      setIsProcessing(true);
      const err = validateFile(file);
      if (err) {
        setValidationError(err);
        setUploadedFile(null);
      } else {
        setUploadedFile(file);
        if (!formData.title.trim()) {
          const base = file.name.replace(/\.[^.]+$/, "");
          setFormData((prev) => ({ ...prev, title: base }));
        }
      }
      setIsProcessing(false);
    },
    [formData.title, validateFile],
  );

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelection(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelection(file);
    e.target.value = "";
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
    setValidationError(null);
  };

  const step2TitleError = useMemo(() => {
    const t = formData.title.trim();
    if (!t) return "Drawing name is required.";
    if (
      existingTitles.some((n) => n.trim().toLowerCase() === t.toLowerCase())
    ) {
      return "This name is already used.";
    }
    return null;
  }, [formData.title, existingTitles]);

  const canGoNext = Boolean(uploadedFile) && !validationError;
  const canSave = !step2TitleError && uploadedFile;

  const handleNext = () => {
    if (!canGoNext) return;
    setCurrentStep(2);
  };

  const handleSave = async () => {
    if (!canSave || !uploadedFile || isSaving) return;
    const fileType = getFileTypeFromFile(uploadedFile);
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave({
        title: formData.title.trim(),
        description: formData.description.trim(),
        isFavorite: formData.isFavorite,
        file: uploadedFile,
        fileUrl: fileObjectUrl,
        fileType,
        previewUrl: fileType === "image" ? fileObjectUrl || "" : "",
      });
      toast.success("Drawing added.");
      handleClose();
    } catch (err) {
      const message =
        err?.message || "Failed to save drawing. Please try again.";
      setSaveError(message);
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const uploadHint =
    "Floor plan: JPG/PNG max 50MB, PDF max 100MB.";

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 700,
          borderRadius: "10px",
          boxShadow: "0px 25px 50px 0px rgba(0,0,0,0.25)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "0.8px solid #E5E7EB",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Roboto, sans-serif",
            fontSize: "18px",
            fontWeight: 600,
            color: "#101828",
          }}
        >
          Add Patrol Drawing
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: "#99A1AF" }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ bgcolor: "#F9FAFB", px: 1.5, py: 0.75 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Box
            sx={{
              flex: 1,
              height: 6,
              borderRadius: "26843500px",
              bgcolor: "#2932E5",
            }}
          />
          <Box
            sx={{
              flex: 1,
              height: 6,
              borderRadius: "26843500px",
              bgcolor: currentStep === 2 ? "#2932E5" : "#D1D5DC",
            }}
          />
        </Box>
      </Box>

      <Box sx={{ p: currentStep === 1 ? 3 : 1.25 }}>
        {currentStep === 1 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {validationError && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "#FEF2F2",
                  border: "1px solid #FECACA",
                }}
              >
                <ErrorOutlineIcon
                  sx={{ color: "#B91C1C", fontSize: 22, flexShrink: 0 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      color: "#991B1B",
                      fontWeight: 500,
                    }}
                  >
                    {validationError}
                  </Typography>
                  <Button
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      setValidationError(null);
                      setUploadedFile(null);
                    }}
                    size="small"
                    sx={{
                      mt: 1,
                      textTransform: "none",
                      color: "#2932E5",
                    }}
                  >
                    Try again
                  </Button>
                </Box>
              </Box>
            )}

            {uploadedFile && !validationError ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: "#22C55E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CheckIcon sx={{ fontSize: 48, color: "white" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    maxWidth: 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {uploadedFile.name}
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#6B7280" }}>
                  {(uploadedFile.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Button
                    variant="contained"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteFile}
                    sx={{
                      textTransform: "none",
                      bgcolor: "#DC2626",
                      "&:hover": { bgcolor: "#B91C1C" },
                    }}
                  >
                    Delete File
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() =>
                      document.getElementById("patrol-drawing-file-input")?.click()
                    }
                    sx={{
                      textTransform: "none",
                      bgcolor: "#2932E5",
                      "&:hover": { bgcolor: "#1f28b8" },
                    }}
                  >
                    Re-upload File
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  border: "1.6px dashed #CBD5E1",
                  borderRadius: "12px",
                  bgcolor: dragActive
                    ? "rgba(41, 50, 229, 0.05)"
                    : "transparent",
                  p: 6,
                  textAlign: "center",
                  cursor: isProcessing ? "wait" : "pointer",
                  minHeight: 251,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: isProcessing ? 0.7 : 1,
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() =>
                  !isProcessing &&
                  document.getElementById("patrol-drawing-file-input")?.click()
                }
              >
                {isProcessing ? (
                  <CircularProgress size={32} sx={{ color: "#2932E5" }} />
                ) : (
                  <>
                    <Box sx={{ mb: 2, color: "#94A3B8" }}>
                      <CloudUploadIcon sx={{ fontSize: 64 }} />
                    </Box>
                    <Typography sx={{ mb: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#2932E5",
                        }}
                      >
                        Click to upload
                      </Box>
                      <Box
                        component="span"
                        sx={{ fontSize: 16, color: "#212121" }}
                      >
                        {" "}
                        or drag and drop
                      </Box>
                    </Typography>
                    <Typography
                      sx={{ fontSize: 14, color: "#64748B", mb: 0.5 }}
                    >
                      {uploadHint}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
                      Max file size: 50MB
                    </Typography>
                  </>
                )}
              </Box>
            )}

            <input
              id="patrol-drawing-file-input"
              type="file"
              accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
              hidden
              onChange={handleFileInputChange}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, p: 1.25 }}>
            {saveError && (
              <Typography sx={{ fontSize: 14, color: "#B91C1C", fontWeight: 500 }}>
                {saveError}
              </Typography>
            )}
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#364153",
                  mb: 1,
                }}
              >
                Drawing name <span style={{ color: "#FB2C36" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                error={Boolean(step2TitleError)}
                helperText={step2TitleError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    bgcolor: "#F9FAFB",
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#364153",
                  mb: 1,
                }}
              >
                Description
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Optional notes about this floor plan…"
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
                }}
              />
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isFavorite}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      isFavorite: e.target.checked,
                    }))
                  }
                  sx={{
                    color: "#2932E5",
                    "&.Mui-checked": { color: "#2932E5" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: "#364153" }}>
                  Favorite
                </Typography>
              }
            />

            {fileObjectUrl && uploadedFile && (
              <Box>
                <Typography
                  sx={{ fontSize: 14, fontWeight: 500, color: "#364153", mb: 1 }}
                >
                  Preview
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 200,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <PatrolDrawingMediaSurface
                    drawing={{
                      fileUrl: fileObjectUrl,
                      fileType: getFileTypeFromFile(uploadedFile),
                      fileName: uploadedFile.name,
                    }}
                    minHeight={200}
                    compact
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          bgcolor: "#F9FAFB",
          borderTop: "0.8px solid #E5E7EB",
        }}
      >
        <Typography sx={{ fontSize: 14, color: "#4A5565" }}>
          Step {currentStep} of 2
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {currentStep === 1 ? (
            <>
              <Button
                onClick={handleClose}
                sx={{
                  bgcolor: "white",
                  border: "0.8px solid rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  color: "#0A0A0A",
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canGoNext}
                sx={{
                  bgcolor: "#2932E5",
                  color: "white",
                  textTransform: "none",
                  borderRadius: "8px",
                  boxShadow: "none",
                  opacity: canGoNext ? 1 : 0.5,
                  "&:hover": { bgcolor: "#1f28b8" },
                }}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleClose}
                sx={{
                  bgcolor: "white",
                  border: "0.8px solid rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => setCurrentStep(1)}
                sx={{
                  bgcolor: "white",
                  border: "0.8px solid rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canSave || isSaving}
                sx={{
                  bgcolor: "#2932E5",
                  color: "white",
                  textTransform: "none",
                  borderRadius: "8px",
                  opacity: canSave && !isSaving ? 1 : 0.5,
                  "&:hover": { bgcolor: "#1f28b8" },
                }}
              >
                {isSaving ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Save"
                )}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
